import { toast } from 'react-toastify';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';

import { ColumnsSectionContainer, SectionColumn } from 'components/DetailsView/Section';

import { Loader } from 'components/Loader';
import { TotemSelect } from 'components/TotemSelect';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { FaTrash } from 'react-icons/fa';
import { colors } from 'constants/colors';
import { useGetAllProductsQuery } from 'data/__generated__';

type FormData = {
    wantedProductIds: string[];
    unwantedProductIds: string[];
};

export const SiteWantedProducts = () => {
    const { setValue, watch } = useFormContext<FormData>();
    const { wantedProductIds, unwantedProductIds } = watch();

    const { data: productsData, loading: productsLoading, error: productsError } = useGetAllProductsQuery();

    if (productsLoading && !productsData) {
        return (
            <ColumnsSectionContainer numberOfColumns={1}>
                <Loader />
            </ColumnsSectionContainer>
        );
    }

    if (productsError || !productsData) {
        throw new Error('Une erreur est survenue lors de la récupération des produits du fournisseur');
    }

    const { getAllProducts: products } = productsData;

    function addWantedProduct(productId: string) {
        if (wantedProductIds.some((wantedProductId: string) => wantedProductId === productId)) {
            toast.info('Le produit est déjà dans la liste');
            return;
        }

        setValue('wantedProductIds', [...wantedProductIds, productId]);
    }

    function removeWantedProduct(productId: string) {
        const newWantedProductIds = wantedProductIds.filter((wantedProductId: string) => {
            return wantedProductId !== productId;
        });

        setValue('wantedProductIds', newWantedProductIds);
    }

    function addUnwantedProduct(productId: string) {
        if (unwantedProductIds.some((unwantedProductId: string) => unwantedProductId === productId)) {
            toast.info('Le produit est déjà dans la liste');
            return;
        }

        setValue('unwantedProductIds', [...unwantedProductIds, productId]);
    }

    function removeUnwantedProduct(productId: string) {
        const newUnwantedProductIds = unwantedProductIds.filter((unwantedProductId: string) => {
            return unwantedProductId !== productId;
        });

        setValue('unwantedProductIds', newUnwantedProductIds);
    }

    const productsOptions =
        products
            .filter(({ _id }) => !wantedProductIds.some((wantedProductId: string) => _id === wantedProductId))
            .map(({ _id, fullname }) => ({
                value: _id,
                label: fullname,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

    return (
        <ColumnsSectionContainer numberOfColumns={2}>
            <SectionColumn>
                <Title>Produit voulu sur site</Title>
                <TotemSelect
                    placeholder="Sélectionner un produit"
                    value={null}
                    options={productsOptions}
                    onChange={(value) => {
                        if (value) {
                            addWantedProduct(value);
                        }
                    }}
                />
                {wantedProductIds.map((wantedProductId: string) => {
                    const product = products.find(({ _id }) => wantedProductId === _id);
                    return (
                        <ProductContainer key={wantedProductId}>
                            <ProductName>{product?.fullname ?? wantedProductId}</ProductName>
                            {!product ? (
                                <ErrorMessage>Le produit n'est pas trouvable en base de données</ErrorMessage>
                            ) : null}

                            <TotemPrimaryButton type="button" onClick={() => removeWantedProduct(wantedProductId)}>
                                <FaTrash size={20} />
                            </TotemPrimaryButton>
                        </ProductContainer>
                    );
                })}
            </SectionColumn>
            <SectionColumn>
                <Title>Produit non voulu sur site</Title>
                <TotemSelect
                    placeholder="Sélectionner un produit"
                    value={null}
                    options={productsOptions}
                    onChange={(value) => {
                        if (value) {
                            addUnwantedProduct(value);
                        }
                    }}
                />
                {unwantedProductIds.map((unwantedProductId: string) => {
                    const product = products.find(({ _id }) => unwantedProductId === _id);
                    return (
                        <ProductContainer key={unwantedProductId}>
                            <ProductName>{product?.fullname ?? unwantedProductId}</ProductName>
                            {!product ? (
                                <ErrorMessage>Le produit n'est pas trouvable en base de données</ErrorMessage>
                            ) : null}

                            <TotemPrimaryButton type="button" onClick={() => removeUnwantedProduct(unwantedProductId)}>
                                <FaTrash size={20} />
                            </TotemPrimaryButton>
                        </ProductContainer>
                    );
                })}
            </SectionColumn>
        </ColumnsSectionContainer>
    );
};

const Title = styled.div`
    font-size: 20px;
    font-weight: bold;
    margin-top: 20px;
`;
const ProductContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 10px;
`;
const ProductName = styled.div`
    font-weight: bold;
    margin-left: 15px;
    flex-grow: 1;
`;

const ErrorMessage = styled.div`
    color: ${colors.red};
`;
